// Paste an instruction, read the parse back, then save.
//
// TWO STEPS, NEVER ONE. Preview parses and writes nothing. Save writes. The
// person sees exactly what was read out of their document, and every question
// the parser could not answer, before it becomes the record of what they are
// held to. Collapsing this into one button would mean discovering a misread
// number later, on a screen somebody else is reading.
//
// The role is editable. It is the one field a person can always answer and the
// parser cannot always find, so it is offered pre-filled rather than demanded or
// guessed at.
import { useState } from 'react'
import { AlertTriangle, Check, X } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Spinner } from '../../lib/shadcn/spinner'
import {
  usePasteOperatorInstruction,
  usePreviewOperatorInstruction,
} from '../../hooks/backend/operator'
import { slotLabel, type MeasureRow, type ParseResult } from '../../utils/operator'

export function PasteInstructionPanel({
  personId,
  personName,
  measures,
  onClose,
  onSaved,
}: {
  personId: string
  personName: string
  measures: MeasureRow[]
  onClose: () => void
  onSaved: () => void
}) {
  const previewFn = usePreviewOperatorInstruction()
  const pasteFn = usePasteOperatorInstruction()

  const [body, setBody] = useState('')
  const [role, setRole] = useState('')
  const [roleTouched, setRoleTouched] = useState(false)
  const [parse, setParse] = useState<ParseResult | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)

  const onPreview = async () => {
    setSaveError(null)
    const res = (await previewFn.trigger({ body_md: body }, { skipCache: true })) as unknown as
      | ParseResult
      | undefined
    const result = res ?? (previewFn.data as ParseResult | undefined) ?? null
    setParse(result)
    if (result?.role_label && !roleTouched) setRole(result.role_label)
  }

  const onSave = async () => {
    setSaveError(null)
    try {
      const res = (await pasteFn.trigger(
        { person_id: personId, body_md: body, role_label: role },
        { skipCache: true },
      )) as unknown as { result?: Record<string, unknown> } | undefined
      const payload = (res ?? (pasteFn.data as { result?: Record<string, unknown> } | undefined))
        ?.result
      if (payload && payload['ok'] === false) {
        setSaveError(String(payload['reason'] ?? 'It was not saved.'))
        return
      }
      onSaved()
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'It was not saved.')
    }
  }

  const canSave =
    parse !== null && parse.status !== 'unreadable' && role.trim() !== '' && !pasteFn.loading

  // WHY SAVE IS OFF, IN WORDS, NEXT TO THE BUTTON.
  //
  // A grey button that gives no reason is a dead end. On 27 August 2026 the
  // reason was an empty role field, twenty lines further up the dialog and out
  // of sight behind a long parse, and the report that came back was "it wont let
  // me save". A control that refuses has to say what it is waiting for.
  const blockedBecause =
    parse === null
      ? 'Read it back before saving.'
      : parse.status === 'unreadable'
        ? 'This document cannot be saved as it stands. The questions above say why.'
        : role.trim() === ''
          ? 'Save is off until the Role field above is filled in. Nothing else is missing.'
          : null

  // Which of the seeded measures could serve a declared number, offered as
  // information only. Binding is a separate, deliberate, admin act; this panel
  // never binds anything, because agreeing what a number means is a conversation
  // and not a side effect of pasting a document.
  const personMeasures = measures.filter((m) => m.scope === 'person')

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
      <div className="w-full max-w-3xl rounded-lg border border-border bg-card shadow-lg">
        <header className="flex items-center gap-2 border-b border-border px-4 py-3">
          <h2 className="text-sm font-semibold text-foreground">
            Instruction for {personName}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="ml-auto rounded p-1 text-muted-foreground hover:bg-muted"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="space-y-4 px-4 py-4">
          <div>
            <label
              htmlFor="operator-body"
              className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
            >
              Paste the whole document
            </label>
            <textarea
              id="operator-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={10}
              placeholder="Paste the full personal operator instruction here, not a section of it."
              className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 font-mono text-xs text-foreground"
            />
            <div className="mt-1 flex items-center gap-2">
              <Button size="sm" onClick={onPreview} disabled={body.trim() === '' || previewFn.loading}>
                {previewFn.loading ? <Spinner className="mr-1.5 h-3.5 w-3.5" /> : null}
                Read it back
              </Button>
              <span className="text-xs text-muted-foreground">
                Nothing is saved by this. It only shows what was read.
              </span>
            </div>
          </div>

          {previewFn.error ? (
            <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-200">
              {previewFn.error}
            </div>
          ) : null}

          {parse ? (
            <>
              <div>
                <label
                  htmlFor="operator-role"
                  className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
                >
                  Role
                </label>
                <Input
                  id="operator-role"
                  value={role}
                  onChange={(e) => {
                    setRole(e.target.value)
                    setRoleTouched(true)
                  }}
                  placeholder="For example, Driver Servant"
                  className="mt-1"
                />
                {parse.role_label === null ? (
                  <p
                    className={`mt-1 text-xs ${
                      role.trim() === ''
                        ? 'font-medium text-amber-700 dark:text-amber-400'
                        : 'text-muted-foreground'
                    }`}
                  >
                    This document does not state a role title, which is normal. Type the title that
                    should be on record. Saving is switched off until you do, and nothing else about
                    the document is affected.
                  </p>
                ) : null}
              </div>

              {parse.status === 'unreadable' ? (
                <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-200">
                  This document cannot be saved as it stands. See the questions below.
                </div>
              ) : null}

              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Numbers read from the document ({parse.metrics.length})
                </h3>
                {parse.metrics.length === 0 ? (
                  <p className="mt-2 text-sm text-muted-foreground">None.</p>
                ) : (
                  <ul className="mt-2 space-y-2">
                    {parse.metrics.map((m) => (
                      <li
                        key={`${m.slot}-${m.slot_index}`}
                        className="rounded-md border border-border px-3 py-2"
                      >
                        <div className="flex items-baseline gap-2">
                          <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                            {slotLabel(m.slot)}
                          </span>
                          <span className="text-sm font-medium text-foreground">{m.name}</span>
                        </div>
                        <dl className="mt-1 grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs text-muted-foreground sm:grid-cols-4">
                          <div>
                            <dt className="inline">Rhythm: </dt>
                            <dd className="inline">{m.cadence ?? 'not stated'}</dd>
                          </div>
                          <div>
                            <dt className="inline">Direction: </dt>
                            <dd className="inline">{m.direction.replace(/_/g, ' ')}</dd>
                          </div>
                          <div>
                            <dt className="inline">Unit: </dt>
                            <dd className="inline">{m.unit ?? 'not stated'}</dd>
                          </div>
                          <div>
                            <dt className="inline">Target: </dt>
                            <dd className="inline">
                              {m.target_value !== null
                                ? m.target_value.toLocaleString('en-ZA')
                                : (m.target_text ?? 'none')}
                            </dd>
                          </div>
                        </dl>
                        {m.definition_text ? (
                          <p className="mt-1 text-xs italic text-muted-foreground">
                            {m.definition_text}
                          </p>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {parse.warnings.length > 0 ? (
                <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 dark:border-amber-900 dark:bg-amber-950">
                  <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-amber-900 dark:text-amber-200">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    What could not be read ({parse.warnings.length})
                  </h3>
                  <ul className="mt-1.5 list-disc space-y-1 pl-5 text-xs text-amber-900 dark:text-amber-100">
                    {parse.warnings.map((w) => (
                      <li key={w}>{w}</li>
                    ))}
                  </ul>
                  <p className="mt-2 text-xs text-amber-900 dark:text-amber-100">
                    These can be saved as they are. Each one is a question that stays open until
                    somebody answers it, and the number will show as declared but not measured until
                    then.
                  </p>
                </div>
              ) : (
                <div className="flex items-center gap-1.5 rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-100">
                  <Check className="h-3.5 w-3.5" />
                  Everything in the numbers section was read with nothing left open.
                </div>
              )}

              <details className="rounded-md border border-border px-3 py-2">
                <summary className="cursor-pointer text-xs font-medium text-muted-foreground">
                  What this platform can currently measure ({personMeasures.length} personal
                  measures)
                </summary>
                <ul className="mt-2 space-y-1.5 text-xs text-muted-foreground">
                  {personMeasures.map((m) => (
                    <li key={m.measure_key}>
                      <span className="font-medium text-foreground">{m.label}</span>{' '}
                      <span>({m.grains.join(', ')})</span>
                      <div>{m.source_note}</div>
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-xs text-muted-foreground">
                  Pasting does not connect a number to any of these. An admin does that separately,
                  with a reason on the record, because agreeing what a number means is a
                  conversation.
                </p>
              </details>
            </>
          ) : null}

          {saveError ? (
            <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-200">
              {saveError}
            </div>
          ) : null}
        </div>

        <footer className="flex items-center gap-2 border-t border-border px-4 py-3">
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <div className="ml-auto flex items-center gap-2">
            {blockedBecause !== null ? (
              <span className="text-xs text-muted-foreground">{blockedBecause}</span>
            ) : null}
            <Button size="sm" onClick={onSave} disabled={!canSave}>
              {pasteFn.loading ? <Spinner className="mr-1.5 h-3.5 w-3.5" /> : null}
              Save this version
            </Button>
          </div>
        </footer>
      </div>
    </div>
  )
}
